import type React from 'react'
import { useState, useCallback } from 'react'
import type { ConversationPlan } from '@knownow/domain'
import { Button } from '@knownow/ui'
import { publishPlan } from '@/lib/plan-api-client'

function isReadyToPublish(plan: ConversationPlan): boolean {
  const activeNeeds = plan.informationNeeds.filter((n) => n.deletedAt === null)
  if (activeNeeds.length === 0) return false
  if (plan.strategy === null) return false
  if (plan.strategy.isStale) return false
  return true
}

function isPublished(plan: ConversationPlan): boolean {
  return plan.status === 'active' && plan.shareToken !== null
}

export interface PublishButtonProps {
  readonly plan: ConversationPlan
  readonly onPublished: () => void
}

export function PublishButton({ plan, onPublished }: PublishButtonProps): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleShare = useCallback(async (): Promise<void> => {
    if (!plan.shareToken) return
    const url = `${window.location.origin}/c/${plan.shareToken}`

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url })
      } catch {
        // User cancelled or share failed — no action needed
      }
      return
    }

    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [plan.shareToken])

  if (isPublished(plan)) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={handleShare}
        data-testid="share-button"
      >
        {copied ? 'Link Copied!' : 'Share'}
      </Button>
    )
  }

  const ready = isReadyToPublish(plan)
  const canPublish = ready && (plan.status === 'reviewing' || (plan.status === 'active' && plan.shareToken === null))

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={!canPublish}
        title={!ready ? 'Add information needs and a strategy to publish' : !canPublish ? 'Plan must be in reviewing or active status to publish' : undefined}
        data-testid="publish-button"
      >
        Publish
      </Button>
      {open && (
        <PublishModal
          plan={plan}
          onClose={() => setOpen(false)}
          onPublished={onPublished}
        />
      )}
    </>
  )
}

interface PublishModalProps {
  readonly plan: ConversationPlan
  readonly onClose: () => void
  readonly onPublished: () => void
}

function PublishModal({ plan, onClose, onPublished }: PublishModalProps): React.ReactElement {
  const [publishing, setPublishing] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handlePublish = useCallback(async (): Promise<void> => {
    setPublishing(true)
    setError(null)

    try {
      const result = await publishPlan(plan.organizationId, plan.id, plan.version)

      const url = `${window.location.origin}/c/${result.shareToken}`
      setShareUrl(url)
      onPublished()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish plan')
      setPublishing(false)
    }
  }, [plan, onPublished])

  const handleCopy = useCallback(async (): Promise<void> => {
    if (!shareUrl) return
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [shareUrl])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50" data-testid="publish-modal">
      <div className="w-full max-w-md rounded-xl bg-neutral-0 p-6 shadow-lg">
        {!shareUrl ? (
          <>
            <h2 className="text-lg font-bold font-serif text-neutral-900 mb-2">
              Publish plan
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              This will make your plan available via a shareable link. Respondents will be able to
              start conversations and provide feedback.
            </p>

            {error && (
              <div className="mb-4 rounded-md bg-error-50 p-3 text-sm text-error-700" data-testid="publish-error">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                data-testid="cancel-publish"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handlePublish}
                disabled={publishing}
                data-testid="confirm-publish"
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold font-serif text-neutral-900 mb-2">
              Plan published
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              Share this link with your respondents:
            </p>

            <div className="flex items-center gap-2 mb-4" data-testid="share-url">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 rounded-md border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm font-mono text-neutral-700"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopy}
                data-testid="copy-url-button"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>

            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={onClose}
                data-testid="close-publish"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
